import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Container } from '@material-ui/core';
import { Add as PlusIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import FooterButton from 'components/FooterButton';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import { parseFiltersForActionLibraryInstances } from 'config/api/actionLibraryInstance/actionLibraryInstance.parsers';
import type { ActionLibraryInstanceListParams } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import checkAccess from 'config/permissions/checkAccess';
import RolesOrganization from 'config/permissions/RolesOrganization';
import RolesPocketSafe from 'config/permissions/RolesPocketSafe';
import useSessionFilters from 'hooks/useSessionFilters';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import PATHS from 'router/PATHS';
import {
  getAssetImportanceFilter,
  getAssetTypeFilter,
  getDueDateUpToFilter,
  getInstanceCreationDateFilter,
  getInstanceStatusFilter,
  getIsOverdueFilter,
  getPastReminderFilter,
  getSubareaFilterForInstances,
  getTagsFilter,
  getValidityFilter,
} from 'services/_filters/parsers/actionLibraryInstanceFilters';
import { getAreaFilter, getCriticalityFilter, getSubareaFilter } from 'services/_filters/parsers/actionTemplateFilters';
import userDataStorage from 'storages/userDataStorage';
import { FilterState, AvailableFilters } from 'types/ActionLibrary';

import ActionLibraryInstancesTable from './_components/ActionLibraryInstancesTable/ActionLibraryInstancesTable';
import EmptyState from './_components/EmptyState/EmptyState';
import ErrorState from './_components/ErrorState/ErrorState';
import InstanceStatistics from './_components/InstanceStatistics';
import useStyles from './ActionLibraryInstancePage.styles';

const ActionLibraryInstancePage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const styles = useStyles();
  const { showHelpDialog } = useHelpDialogContext();

  const userData = userDataStorage.get() ?? {};
  const canCreateActionLibraryInstance =
    checkAccess(userData.ps_roles ?? [], [RolesPocketSafe.ActionLibraryAdministrator]) ||
    checkAccess(userData.roles ?? [], [RolesOrganization.AssetsManager]) ||
    Boolean(userData.is_asset_owner);

  const [filters, setFilters] = useSessionFilters<FilterState>('ACTION_LIBRARY_INSTANCE_FILTERS', {});
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters | null>(null);
  const [loadingFiltersError, setLoadingFiltersError] = useState<Error | null>(null);
  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({
    key: null,
    direction: null,
  });
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const subareaLoadedForAreaRef = useRef<string | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const pageSize = 20;

  const onSort = (key: string, direction: string) => {
    setSort({ key, direction });
  };

  const parsedFilters = useMemo(() => parseFiltersForActionLibraryInstances(filters), [filters]);

  const instanceListOrdering = useMemo(() => {
    if (!sort.key || !sort.direction) return undefined;
    const apiOrderingKeyMap: Record<string, string> = {
      title: 'title',
      area: 'area',
      subArea: 'sub_area',
      assetName: 'asset__name',
      assetType: 'asset__type',
      assetImportance: 'asset_importance',
      criticality: 'criticality',
      deadline: 'deadline',
      status: 'status',
      owner: 'responsible_user',
      createdAt: 'created_at',
      validity: 'validity',
      stepsCount: 'steps_count',
      reportTo: 'reported_to',
    };
    const apiKey = apiOrderingKeyMap[sort.key];
    if (!apiKey) return undefined;
    const prefix = sort.direction === 'desc' ? '-' : '';
    return `${prefix}${apiKey}`;
  }, [sort.key, sort.direction]);

  const metricsParams = useMemo(
    () => ({
      ...(searchTerm && { search: searchTerm }),
      ...parsedFilters,
    }),
    [searchTerm, parsedFilters],
  );

  const exportListParams = useMemo(
    (): Omit<ActionLibraryInstanceListParams, 'limit' | 'offset'> => ({
      ...(searchTerm && { search: searchTerm }),
      ...(instanceListOrdering && { ordering: instanceListOrdering }),
      ...parsedFilters,
    }),
    [parsedFilters, instanceListOrdering, searchTerm],
  );

  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: currentOffset,
      ...exportListParams,
    }),
    [exportListParams, currentOffset, pageSize],
  );

  const {
    data: queryData,
    isLoading: loading,
    error,
  } = useQuery(
    ['ACTION_LIBRARY_INSTANCES_GET_ALL', queryParams],
    async () => {
      const { data } = await actionLibraryInstancesApi.getAllActionLibraryInstances(queryParams);
      return data;
    },
    {
      enabled: !!availableFilters,
      keepPreviousData: true,
      refetchInterval: 5000,
    },
  );

  const tableData = queryData?.results || [];
  const hasNextPage = queryData?.next !== null;
  const listTotalCount = queryData?.count ?? 0;

  const getTableData = useCallback(() => {
    setCurrentOffset(prev => prev + pageSize);
  }, [pageSize]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [areaFilter, criticalityFilter, assetImportanceFilter, assetTypeFilter, subareaFilter] = await Promise.all([
          getAreaFilter(),
          getCriticalityFilter(),
          getAssetImportanceFilter(),
          getAssetTypeFilter(),
          getSubareaFilterForInstances(),
        ]);

        const statusFilter = getInstanceStatusFilter();
        const dueDateUpToFilter = getDueDateUpToFilter();
        const isOverdueFilter = getIsOverdueFilter();
        const pastReminderFilter = getPastReminderFilter();
        const instanceCreationDateFilter = getInstanceCreationDateFilter();
        const validityFilter = getValidityFilter();
        const tagsFilter = getTagsFilter();

        setAvailableFilters({
          area: { ...areaFilter, isSingleSelect: true },
          subarea: { ...subareaFilter, isSingleSelect: true },
          criticality: criticalityFilter,
          asset_importance: assetImportanceFilter,
          is_overdue: isOverdueFilter,
          due_date_up_to: dueDateUpToFilter,
          past_reminder: pastReminderFilter,
          asset_type: assetTypeFilter,
          validity: validityFilter,
          tags: tagsFilter,
          status: statusFilter,
          instance_creation_date: instanceCreationDateFilter,
        } as unknown as AvailableFilters);

        // If session had area selected, load subarea options now (avoids extra useEffect dep on availableFilters)
        const currentFilters = filtersRef.current;
        if (currentFilters?.area?.length) {
          const [firstArea] = currentFilters.area;
          const raw =
            (firstArea as { id?: unknown; value?: unknown; name?: unknown })?.id ??
            (firstArea as { value?: unknown })?.value ??
            (firstArea as { name?: unknown })?.name ??
            firstArea;
          const areaId = typeof raw === 'string' || typeof raw === 'number' ? String(raw) : '';
          if (areaId) {
            getSubareaFilter(areaId).then(subareaData => {
              setAvailableFilters(prev =>
                prev ? ({ ...prev, subarea: { ...subareaData, isSingleSelect: true } } as unknown as AvailableFilters) : prev,
              );
            });
          }
        }
      } catch (err) {
        setLoadingFiltersError(err as Error);
      }
    };

    loadFilters();
  }, []);

  // Load subarea options when area selection changes (no availableFilters in deps to avoid loop)
  useEffect(() => {
    if (!filters.area?.length) {
      subareaLoadedForAreaRef.current = null;
      getSubareaFilterForInstances().then(subareaData => {
        setAvailableFilters(prev =>
          prev ? ({ ...prev, subarea: { ...subareaData, isSingleSelect: true } } as unknown as AvailableFilters) : prev,
        );
      });
      return;
    }
    if (!availableFilters) return;
    const [firstArea] = filters.area;
    const raw =
      (firstArea as { id?: unknown; value?: unknown; name?: unknown })?.id ??
      (firstArea as { value?: unknown })?.value ??
      (firstArea as { name?: unknown })?.name ??
      firstArea;
    const areaId = typeof raw === 'string' || typeof raw === 'number' ? String(raw) : '';
    if (!areaId) return;
    if (subareaLoadedForAreaRef.current === areaId) return;
    subareaLoadedForAreaRef.current = areaId;
    getSubareaFilter(areaId).then(subareaData => {
      setAvailableFilters(prev =>
        prev ? ({ ...prev, subarea: { ...subareaData, isSingleSelect: true } } as unknown as AvailableFilters) : prev,
      );
    });
  }, [filters.area]);

  // Reset offset when filters, sort, search or myInstancesOnly change
  useEffect(() => {
    setCurrentOffset(0);
  }, [filters, sort, searchTerm]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleCreateClick = () => {
    history.push(PATHS.ACTION_LIBRARY_INSTANCE_CREATE);
  };

  const openHelpDialog = () => {
    showHelpDialog({
      title: t(action_library_messages.instance.help.title),
      body: t(action_library_messages.instance.help.body),
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  if (error || loadingFiltersError) {
    return (
      <Container>
        <ErrorState onRetry={() => window.location.reload()} />
      </Container>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          rightAdornment={
            <>
              <TextButton onClick={openHelpDialog}>{t(general_messages.how_it_works)}</TextButton>
              {canCreateActionLibraryInstance ? (
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={false}
                  icon={false}
                  onClick={handleCreateClick}
                  showLoader={false}
                  textLike={false}
                  variant='outlined'
                >
                  <PlusIcon className={styles.iconInButton} fontSize='small' />
                  {t(action_library_messages.instance.create_button)}
                </ColoredButton>
              ) : null}
            </>
          }
        >
          {t(action_library_messages.instance.page_title)}
        </ManagePageHeader>

        {!availableFilters ? (
          <Loader inner />
        ) : (
          <>
            <InstanceStatistics metricsParams={metricsParams} />
            <ActionLibraryInstancesTable
              canExportCsv={canCreateActionLibraryInstance}
              data={tableData}
              exportListParams={exportListParams}
              filtersService={{ availableFilters, filters, setFilters }}
              listTotalCount={listTotalCount}
              loading={loading}
              onSearch={handleSearch}
              onSort={onSort}
              searchValue={searchTerm}
              sort={sort}
            />
            {tableData && tableData.length === 0 && !loading && (
              <EmptyState
                descriptionMessage={action_library_messages.instance.empty_state.description}
                onResetFilters={handleResetFilters}
                resetFiltersMessage={action_library_messages.instance.empty_state.reset_filters}
                titleMessage={action_library_messages.instance.empty_state.title}
              />
            )}

            {hasNextPage && (
              <FooterButton className='' loading={loading} onClick={getTableData}>
                {t(action_library_messages.table.show_more)}
              </FooterButton>
            )}
          </>
        )}
      </Container>
    </ErrorBoundary>
  );
};

export default ActionLibraryInstancePage;
